/**
 * useCustomerList Hook
 * Centralized state management and business logic for customer list
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Customer, CreateCustomerDto, UpdateCustomerDto } from '../../../types/customer';
import { useCustomers } from '../../../hooks/useCustomers';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

export function useCustomerList() {
  const navigate = useNavigate();

  // Modal state
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Delete confirmation state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(null);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch customers data using existing hook
  const customersData = useCustomers({
    initialPage: 1,
    initialLimit: 10,
  });

  // Modal handlers
  const handleOpenAddModal = useCallback(() => {
    setSelectedCustomer(null);
    setIsEditMode(false);
    setIsModalOpen(true);
  }, []);

  const handleOpenEditModal = useCallback((customer: Customer) => {
    setSelectedCustomer(customer);
    setIsEditMode(true);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedCustomer(null);
    setIsEditMode(false);
  }, []);

  const handleSubmit = useCallback(async (data: CreateCustomerDto | UpdateCustomerDto) => {
    try {
      if (isEditMode && selectedCustomer) {
        await customersData.updateCustomer({ id: selectedCustomer.id, data });
        setSnackbar({
          open: true,
          message: 'Customer updated successfully',
          severity: 'success',
        });
      } else {
        await customersData.createCustomer(data as CreateCustomerDto);
        setSnackbar({
          open: true,
          message: 'Customer created successfully',
          severity: 'success',
        });
      }
      handleCloseModal();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Operation failed',
        severity: 'error',
      });
    }
  }, [isEditMode, selectedCustomer, customersData, handleCloseModal]);

  // Navigation
  const handleRowClick = useCallback((customerId: string) => {
    navigate(`/customers/${customerId}`);
  }, [navigate]);

  // Delete handlers
  const handleDeleteClick = useCallback((event: React.MouseEvent, customer: Customer) => {
    event.stopPropagation();
    setCustomerToDelete(customer);
    setDeleteModalOpen(true);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setDeleteModalOpen(false);
    setCustomerToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!customerToDelete) return;

    try {
      // SCHEMA MIGRATION: No force parameter needed - automatic vehicle cleanup
      await customersData.deleteCustomer({
        id: customerToDelete.id
      });

      setSnackbar({
        open: true,
        message: `Customer "${customerToDelete.name}" deleted successfully`,
        severity: 'success',
      });

      setDeleteModalOpen(false);
      setCustomerToDelete(null);
    } catch (error: any) {
      console.error('Failed to delete customer:', error);

      // Show error message
      let errorMessage = 'Failed to delete customer. Please try again.';
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });

      setDeleteModalOpen(false);
      setCustomerToDelete(null);
    }
  }, [customerToDelete, customersData]);

  // Snackbar handler
  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  return {
    // Customer data
    ...customersData,

    // Modal state
    selectedCustomer,
    isModalOpen,
    isEditMode,

    // Delete state
    deleteModalOpen,
    customerToDelete,

    // Snackbar state
    snackbar,

    // Modal handlers
    handleOpenAddModal,
    handleOpenEditModal,
    handleCloseModal,
    handleSubmit,

    // Navigation
    handleRowClick,

    // Delete handlers
    handleDeleteClick,
    handleCancelDelete,
    handleConfirmDelete,

    // Snackbar handler
    handleCloseSnackbar,
  };
}
